// src/pages/admin/agents/AgentDetailsPage.tsx
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Edit,
  Trash2,
  Mail,
  Phone,
  Building,
  Users,
  AlertCircle,
  User,
  Briefcase,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  Shield,
  Key,
} from "lucide-react";
import DeleteConfirmationModal from "../../../components/ui/DeleteConfirmationModal";
import { alertError, alertSuccess } from "../../../helpers/alertError";
import { agentService } from "../../../services/agentService";
import { Agent, AgentModulePermission } from "../../../types/features/Agent";
import useToken from "../../../hooks/features/auth/useToken";
import useEntreprises from "../../../hooks/features/entreprises/useEntreprises";
import { Entreprise } from "../../../types/features/Entreprise";

export default function AgentDetailsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const agent = location.state as Agent;
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [agentEntreprise, setAgentEntreprise] = useState<Entreprise | null>(null);
  const [agentPermissions, setAgentPermissions] = useState<AgentModulePermission[]>([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);

  const { getEntrepriseById } = useEntreprises();
  const { token } = useToken();

  useEffect(() => {
    const getAgentEntreprise = async () => {
      if (!agent.entreprise_id) return;
      const entrepriseData = await getEntrepriseById(agent.entreprise_id);
      if (entrepriseData) {
        setAgentEntreprise(entrepriseData);
      }
    };

    getAgentEntreprise();
    loadAgentPermissions();
  }, [agent.entreprise_id, agent.id, getEntrepriseById]);

  const loadAgentPermissions = async () => {
    if (!agent || !token) return;
    setLoadingPermissions(true);
    try {
      const data = await agentService.getAgentPermissions(token, agent.id);
      if (data) {
        setAgentPermissions(data.permissions || []);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des permissions", error);
    } finally {
      setLoadingPermissions(false);
    }
  };

  if (!agent) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6 animate-fade-in-up">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle size={32} className="text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Agent non trouvé</h2>
          <p className="text-gray-500 mb-4">Les informations de l'agent sont introuvables.</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-gradient-to-r from-primary to-primary/80 text-white rounded-xl hover:shadow-md transition-all duration-300"
          >
            Retour
          </button>
        </div>
      </div>
    );
  }

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status: "ACTIVE" | "INACTIVE") => {
    if (status === "ACTIVE") {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
          <CheckCircle size={14} />
          Actif
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
        <XCircle size={14} />
        Inactif
      </span>
    );
  };

  const handleUpdate = () => {
    navigate("/admin/agents/update", { state: agent });
  };

  const handleDelete = async () => {
    if (!agent.id) {
      alertError("Une erreur est survenue");
      return;
    }
    setIsDeleting(true);
    try {
      await agentService.deleteAgent(token, agent.id);
      setOpenDeleteModal(false);
      alertSuccess("Agent supprimé avec succès");
      setTimeout(() => {
        navigate("/admin/agents");
      }, 1500);
    } catch (error) {
      alertError("Erreur lors de la suppression");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      {/* En-tête */}
      <div className="bg-white border-b border-gray-100 shadow-sm sticky top-0 z-10 animate-fade-in-up">
        <div className="px-6 py-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-xl transition-all duration-300 group"
              >
                <ArrowLeft size={20} className="text-gray-600 group-hover:text-primary transition-colors" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">{agent.full_name}</h1>
                <p className="text-sm text-gray-500 mt-1 flex items-center gap-2">
                  <User size={14} />
                  Agent • {agent.departement || "Aucun département"}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleUpdate}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-300"
              >
                <Edit size={16} />
                Modifier
              </button>
              <button
                onClick={() => setOpenDeleteModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all duration-300"
              >
                <Trash2 size={16} />
                Supprimer
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu */}
      <div className="p-6">
        <div className="max-w-5xl mx-auto space-y-6">
          {/* Statut */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden animate-fade-in-up" style={{ animationDelay: '100ms' }}>
            <div className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-xl ${agent.status === "ACTIVE" ? "bg-green-100" : "bg-red-100"}`}>
                  {agent.status === "ACTIVE" ? (
                    <CheckCircle size={24} className="text-green-600" />
                  ) : (
                    <XCircle size={24} className="text-red-600" />
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-500">Statut</p>
                  {getStatusBadge(agent.status)}
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Clock size={14} />
                {agent.updated_at && `Dernière mise à jour: ${formatDate(agent.updated_at)}`}
              </div>
            </div>
          </div>

          {/* Informations personnelles */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden animate-fade-in-up" style={{ animationDelay: '200ms' }}>
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <User size={18} className="text-primary" />
                Informations personnelles
              </h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <User size={16} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Nom complet</p>
                    <p className="font-semibold text-gray-800">{agent.full_name}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Mail size={16} className="text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="font-semibold text-gray-800">{agent.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Phone size={16} className="text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Téléphone</p>
                    <p className="font-semibold text-gray-800">{agent.phonenumber || "Non renseigné"}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Calendar size={16} className="text-orange-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Date de création</p>
                    <p className="font-semibold text-gray-800">{formatDate(agent.created_at)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Informations professionnelles */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden animate-fade-in-up" style={{ animationDelay: '300ms' }}>
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Briefcase size={18} className="text-primary" />
                Informations professionnelles
              </h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <Building size={16} className="text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Entreprise</p>
                    <p className="font-semibold text-gray-800">
                      {agentEntreprise?.name || "Non assigné"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="p-2 bg-pink-100 rounded-lg">
                    <Users size={16} className="text-pink-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Département</p>
                    <p className="font-semibold text-gray-800">{agent.departement || "Non défini"}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Permissions des modules */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden animate-fade-in-up" style={{ animationDelay: '350ms' }}>
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Shield size={18} className="text-primary" />
                Modules autorisés
              </h2>
            </div>
            <div className="p-6">
              {loadingPermissions ? (
                <div className="flex justify-center py-8">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : agentPermissions.length === 0 ? (
                <p className="text-center text-gray-500 py-4">
                  Aucun module autorisé pour cet agent
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {agentPermissions.map((permission) => (
                    <div
                      key={permission.id}
                      className="border border-gray-200 rounded-xl p-4 hover:border-primary/30 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Key size={16} className="text-primary" />
                          <span className="font-semibold text-gray-800">{permission.module_name}</span>
                        </div>
                        <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
                          {permission.endpoints?.length || 0}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {(permission.endpoints || []).map((endpoint, index) => (
                          <span
                            key={index}
                            className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded font-mono"
                          >
                            {endpoint}
                          </span>
                        ))}
                        {(!permission.endpoints || permission.endpoints.length === 0) && (
                          <span className="text-xs text-gray-400 italic">Aucune autorisation</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Responsabilités */}
          {agent.responsabilities && agent.responsabilities.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden animate-fade-in-up" style={{ animationDelay: '400ms' }}>
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <CheckCircle size={18} className="text-primary" />
                  Responsabilités
                </h2>
              </div>
              <div className="p-6">
                <div className="flex flex-wrap gap-2">
                  {agent.responsabilities.map((resp, index) => (
                    <span
                      key={index}
                      className="px-4 py-2 bg-primary/10 text-primary rounded-xl text-sm font-medium"
                    >
                      {resp}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de suppression */}
      <DeleteConfirmationModal
        isOpen={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        onConfirm={handleDelete}
        title="Supprimer l'agent"
        message={`Êtes-vous sûr de vouloir supprimer l'agent "${agent.full_name}" ? Cette action est irréversible.`}
        confirmText={isDeleting ? "Suppression..." : "Supprimer"}
        cancelText="Annuler"
      />

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fadeInUp 0.5s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  );
}